import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  BookOpen,
  FileText,
  Download,
  CheckCircle,
  AlertCircle,
  List,
  Layout,
  Type,
  Image as ImageIcon,
  File,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';

const PlantillaLibro = () => {
  const requisitosGenerales = [
    {
      titulo: "Formato del documento",
      items: [
        "Tamaño de página: Carta (21.59 cm x 27.94 cm)",
        "Márgenes: Superior 2.5 cm, Inferior 2.5 cm, Izquierdo 3 cm, Derecho 2.5 cm",
        "Orientación: Vertical",
        "Encabezado: 1.25 cm desde el borde",
        "Pie de página: 1.25 cm desde el borde"
      ]
    },
    {
      titulo: "Tipografía",
      items: [
        "Fuente principal: Times New Roman",
        "Tamaño de fuente del texto: 12 puntos",
        "Interlineado: 1.5 líneas",
        "Alineación: Justificada",
        "Sangría de primera línea: 1.27 cm (0.5 pulgadas)"
      ]
    },
    {
      titulo: "Estructura del documento",
      items: [
        "Portada",
        "Página de créditos (reverso de portada)",
        "Índice general",
        "Prólogo o presentación (opcional)",
        "Introducción",
        "Capítulos del contenido principal",
        "Conclusiones",
        "Referencias bibliográficas",
        "Anexos (opcional)",
        "Contraportada"
      ]
    }
  ];

  const elementosPortada = [
    "Título del libro (centrado, mayúsculas, Times New Roman 18 pts, negritas)",
    "Subtítulo (si aplica, Times New Roman 14 pts)",
    "Nombre del autor o autores (Times New Roman 14 pts)",
    "Logotipo institucional del CENID",
    "Pie de imprenta: Editorial CENID, Ciudad, País, Año"
  ];

  const paginaCreditos = [
    "Título completo del libro",
    "Nombre completo del autor(es)",
    "ISBN (será asignado por el CENID)",
    "Derechos de autor (©)",
    "Editorial CENID A.C.",
    "Dirección institucional completa",
    "Correo electrónico institucional",
    "Primera edición y año de publicación",
    "Número total de ejemplares (tiraje)",
    "Diseño de portada (créditos)",
    "Prohibición de reproducción sin autorización"
  ];

  const formatoCapitulos = [
    {
      elemento: "Títulos de capítulos",
      especificacion: "Times New Roman 16 pts, negritas, centrado, numeración romana o arábiga"
    },
    {
      elemento: "Subtítulos nivel 1",
      especificacion: "Times New Roman 14 pts, negritas, alineado a la izquierda"
    },
    {
      elemento: "Subtítulos nivel 2",
      especificacion: "Times New Roman 12 pts, negritas, alineado a la izquierda"
    },
    {
      elemento: "Texto del cuerpo",
      especificacion: "Times New Roman 12 pts, justificado, interlineado 1.5"
    },
    {
      elemento: "Notas al pie",
      especificacion: "Times New Roman 10 pts, numeración consecutiva por capítulo"
    }
  ];

  const requisitosImagenes = [
    "Resolución mínima: 300 dpi para impresión",
    "Formato preferente: JPG, PNG o TIFF",
    "Posición: Centradas con respecto al texto",
    "Numeración: Continua o por capítulo (Figura 1, Figura 2, etc.)",
    "Pie de figura: Times New Roman 10 pts, cursiva, centrado",
    "Las imágenes deben estar libres de derechos o contar con los permisos correspondientes"
  ];

  const tablasGraficos = [
    "Numeración consecutiva (Tabla 1, Gráfico 1, etc.)",
    "Título descriptivo: Times New Roman 10 pts, negritas, centrado arriba de la tabla",
    "Fuente de datos: Times New Roman 10 pts, cursiva, debajo de la tabla",
    "Bordes simples en color negro",
    "Contenido: Times New Roman 10 pts",
    "Alineación centrada para números, izquierda para texto"
  ];

  const citasReferencias = [
    "Sistema de citación: APA 7ª edición (preferentemente) o el acordado con el editor",
    "Citas textuales cortas (menos de 40 palabras): Entre comillas dentro del párrafo",
    "Citas textuales largas (40 palabras o más): Bloque sangrado a 1.27 cm sin comillas, Times New Roman 11 pts",
    "Referencias bibliográficas: Al final del libro, orden alfabético por apellido del autor",
    "Sangría francesa de 1.27 cm en las referencias",
    "Interlineado sencillo dentro de cada referencia, doble espacio entre referencias"
  ];

  const entregaArchivos = [
    {
      item: "Formato digital",
      descripcion: "Microsoft Word (.docx) o Google Docs"
    },
    {
      item: "Imágenes por separado",
      descripcion: "Carpeta con todas las imágenes en alta resolución"
    }
  ];

  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
              <BookOpen className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Plantilla para Publicación de Libros
              </h1>
              <p className="text-xl text-green-100">
                Editorial CENID - Lineamientos y Requisitos Técnicos
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <main className="flex-1 min-w-0">
          <div className="space-y-12">

            {/* Introducción */}
            <section id="introduccion" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Acerca de esta guía</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Esta plantilla ha sido diseñada para estandarizar la presentación de libros académicos
                      y de divulgación científica en la Editorial del Centro de Investigación e Innovación para
                      el Desarrollo Educativo (CENID).
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      El cumplimiento de estos lineamientos es fundamental para garantizar la calidad editorial
                      y facilitar el proceso de publicación.
                    </p>
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                      <p className="text-sm text-amber-900">
                        <strong>Importante:</strong> Todos los manuscritos deben seguir estas especificaciones
                        antes de ser enviados para evaluación editorial.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Requisitos Generales */}
            <section id="requisitos-generales" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-8 h-8 text-[#0b742f]" />
                  <h2 className="text-3xl font-bold text-gray-900">Requisitos Generales del Documento</h2>
                </div>

                <div className="space-y-6">
                  {requisitosGenerales.map((seccion, index) => (
                    <div key={index} className="border-l-4 border-[#0b742f] pl-6 py-2">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">{seccion.titulo}</h3>
                      <ul className="space-y-2">
                        {seccion.items.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Portada */}
            <section id="portada" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Layout className="w-8 h-8 text-[#e87729]" />
                  <h2 className="text-3xl font-bold text-gray-900">Elementos de la Portada</h2>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 mb-6">
                  <p className="text-gray-700 mb-4">
                    La portada es la primera impresión de su obra. Debe incluir los siguientes elementos
                    en el orden establecido:
                  </p>
                  <ul className="space-y-3">
                    {elementosPortada.map((elemento, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold mt-0.5">
                          {index + 1}
                        </div>
                        <span className="text-gray-700">{elemento}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Página de Créditos */}
            <section id="pagina-creditos" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <File className="w-8 h-8 text-blue-600" />
                  <h2 className="text-3xl font-bold text-gray-900">Página de Créditos</h2>
                </div>

                <p className="text-gray-700 mb-6">
                  El reverso de la portada debe contener la siguiente información legal y editorial:
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  {paginaCreditos.map((credito, index) => (
                    <div
                      key={index}
                      className="bg-blue-50 rounded-lg p-4 border border-blue-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{credito}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Formato de Capítulos */}
            <section id="formato-capitulos" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Type className="w-8 h-8 text-purple-600" />
                  <h2 className="text-3xl font-bold text-gray-900">Formato de Capítulos y Secciones</h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                        <th className="px-6 py-4 text-left font-semibold">Elemento</th>
                        <th className="px-6 py-4 text-left font-semibold">Especificación</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {formatoCapitulos.map((item, index) => (
                        <tr
                          key={index}
                          className={index % 2 === 0 ? 'bg-purple-50' : 'bg-white'}
                        >
                          <td className="px-6 py-4 font-semibold text-gray-900">{item.elemento}</td>
                          <td className="px-6 py-4 text-gray-700">{item.especificacion}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Imágenes y Figuras */}
            <section id="imagenes-figuras" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <ImageIcon className="w-8 h-8 text-pink-600" />
                  <h2 className="text-3xl font-bold text-gray-900">Imágenes y Figuras</h2>
                </div>

                <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Requisitos técnicos para imágenes:</h3>
                  <ul className="space-y-3">
                    {requisitosImagenes.map((requisito, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{requisito}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Tablas y Gráficos */}
            <section id="tablas-graficos" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <List className="w-8 h-8 text-indigo-600" />
                  <h2 className="text-3xl font-bold text-gray-900">Tablas y Gráficos</h2>
                </div>

                <div className="space-y-3">
                  {tablasGraficos.map((item, index) => (
                    <div
                      key={index}
                      className="bg-indigo-50 rounded-lg p-4 border-l-4 border-indigo-600"
                    >
                      <p className="text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Citas y Referencias */}
            <section id="citas-referencias" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <BookOpen className="w-8 h-8 text-teal-600" />
                  <h2 className="text-3xl font-bold text-gray-900">Citas y Referencias Bibliográficas</h2>
                </div>

                <div className="space-y-4">
                  {citasReferencias.map((item, index) => (
                    <div
                      key={index}
                      className="bg-teal-50 rounded-lg p-5 border border-teal-200 hover:shadow-md transition-shadow"
                    >
                      <p className="text-gray-700 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-3">Ejemplo de referencia APA 7ª edición:</h3>
                  <div className="bg-white/20 rounded-lg p-4 font-mono text-sm backdrop-blur-sm">
                    <p>Apellido, N. (Año). <em>Título del libro en cursiva</em>. Editorial.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Entrega de Archivos */}
            <section id="entrega-archivos" className="scroll-mt-32">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Download className="w-8 h-8 text-green-600" />
                  <h2 className="text-3xl font-bold text-gray-900">Entrega de Archivos</h2>
                </div>

                <p className="text-gray-700 mb-6">
                  Para facilitar el proceso editorial, los autores deben entregar los siguientes archivos:
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {entregaArchivos.map((archivo, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200 hover:border-green-400 transition-colors"
                    >
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{archivo.item}</h3>
                      <p className="text-gray-700 text-sm">{archivo.descripcion}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contacto y Descarga */}
            <section id="contacto-descarga" className="scroll-mt-32">
              <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-2xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">¿Listo para publicar con nosotros?</h2>
                  <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                    Si tienes dudas sobre estos lineamientos o deseas enviar tu manuscrito, contáctanos
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Contacto */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <File className="w-6 h-6" />
                      Información Editorial
                    </h3>
                    <div className="space-y-3 text-green-50">
                      <p>
                        <strong>Correo:</strong>{' '}
                        <a href="mailto:editorial@cenid.org" className="hover:text-white transition-colors underline">
                          editorial@cenid.org
                        </a>
                      </p>
                      <p>
                        <strong>Sitio web:</strong>{' '}
                        <a
                          href="https://www.cenid.org"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-white transition-colors underline inline-flex items-center gap-1"
                        >
                          www.cenid.org
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Documentos */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Download className="w-6 h-6" />
                      Recursos Disponibles
                    </h3>
                    <div className="space-y-2">
                      <Link
                        to="/editorial/ejemplocapitulo"
                        className="block bg-white/20 hover:bg-white/30 rounded-lg p-3 transition-all duration-300 hover:scale-105"
                      >
                        <p className="font-semibold">📑 Ejemplo de capítulo en línea</p>
                        <p className="text-sm text-green-100">Consulta la estructura y formato requerido</p>
                      </Link>
                      <a
                        href="#"
                        className="block bg-white/20 hover:bg-white/30 rounded-lg p-3 transition-all duration-300 hover:scale-105"
                      >
                        <p className="font-semibold">📋 Guía de estilo APA 7ª edición</p>
                        <p className="text-sm text-green-100">Referencia rápida</p>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </main>
      </div>
    </div>
  );
};

export default PlantillaLibro;
